import { z } from 'zod';

import { AdjustmentType } from '../generated/enums.js';

// ============================================================================
// VALUE DIFFERENCE EXPLANATION - Input/Output Types
// ============================================================================

/**
 * Input for the Value Difference Explanation Agent
 * Used when there's a significant difference between transaction and candidate amounts
 */
export interface ValueDifferenceExplanationInput {
  /** Transaction data */
  transaction: {
    id: string;
    /** Amount in reais (not cents) */
    amount: number;
    postedAt: string;
    description: string;
    counterpartyName?: string;
    counterpartyDocument?: string;
    paymentMethod?: string;
  };

  /** Best match candidate */
  candidate: {
    id: string;
    type: 'AP_PAYMENT' | 'AP_BILL' | 'AR_INVOICE';
    /** Original amount in reais */
    amount: number;
    /** Net amount after known deductions (if available) */
    netAmount?: number;
    date: string;
    dueDate?: string;
    description?: string;
    vendorDocument?: string;
    vendorName?: string;
    customerDocument?: string;
    customerName?: string;
    /** Category helps identify if it's a service (subject to withholdings) */
    category?: string;
    /** Known withholdings from the bill (in reais) */
    knownWithholdings?: {
      irrf?: number;
      iss?: number;
      inss?: number;
      pis?: number;
      cofins?: number;
      csll?: number;
    };
  };

  /** The difference to explain */
  difference: {
    amount: number;
    /** Percentage difference */
    percentDiff: number;
    /** Direction of the difference */
    direction: 'TRANSACTION_HIGHER' | 'TRANSACTION_LOWER';
  };

  /** Additional context */
  context?: {
    /** Days late (if payment after due date) */
    daysLate?: number;
    /** Days early (if payment before due date) */
    daysEarly?: number;
    /** Whether the bill is for services (subject to tax withholdings) */
    isService?: boolean;
    /** Whether this is an installment payment */
    isInstallment?: boolean;
    /** Installment number */
    installmentNumber?: number;
    /** Total installments */
    installmentTotal?: number;
  };
}

/**
 * Scenario types that can explain value differences
 */
export const ValueDifferenceScenarioType = {
  /** Tax withholding (IRRF, ISS, INSS, etc.) */
  WITHHOLDING_TAX: 'WITHHOLDING_TAX',
  /** Late payment with interest/penalty */
  LATE_PAYMENT: 'LATE_PAYMENT',
  /** Early payment with discount */
  EARLY_PAYMENT: 'EARLY_PAYMENT',
  /** Bank fee */
  BANK_FEE: 'BANK_FEE',
  /** IOF tax (credit card/financing) */
  IOF: 'IOF',
  /** Small rounding difference */
  ROUNDING: 'ROUNDING',
  /** Partial payment */
  PARTIAL_PAYMENT: 'PARTIAL_PAYMENT',
  /** Currency exchange variation */
  FX_VARIATION: 'FX_VARIATION',
  /** Marketplace fee */
  MARKETPLACE_FEE: 'MARKETPLACE_FEE',
  /** Negotiated discount */
  NEGOTIATED_DISCOUNT: 'NEGOTIATED_DISCOUNT',
  /** Cannot identify the cause */
  UNKNOWN: 'UNKNOWN',
} as const;

export type ValueDifferenceScenarioType =
  (typeof ValueDifferenceScenarioType)[keyof typeof ValueDifferenceScenarioType];

/**
 * Recommendation for how to handle the value difference
 */
export const ValueDifferenceRecommendation = {
  /** Reconcile with adjustments */
  RECONCILE_WITH_ADJUSTMENTS: 'RECONCILE_WITH_ADJUSTMENTS',
  /** Create separate bills for each adjustment type */
  CREATE_SEPARATE_BILLS: 'CREATE_SEPARATE_BILLS',
  /** Needs manual review */
  MANUAL_REVIEW: 'MANUAL_REVIEW',
  /** This is not the correct match */
  REJECT_MATCH: 'REJECT_MATCH',
} as const;

export type ValueDifferenceRecommendation =
  (typeof ValueDifferenceRecommendation)[keyof typeof ValueDifferenceRecommendation];

/**
 * Suggested adjustment from the AI
 */
export interface SuggestedValueAdjustment {
  /** Adjustment type */
  type: AdjustmentType;
  amount: number;
  description: string;
  /** Whether to create a separate AP bill for this adjustment */
  createSeparateBill?: {
    suggested: boolean;
    reason: string;
    /** Suggested vendor for the bill (e.g., "Receita Federal" for IRRF) */
    suggestedVendorName?: string;
    /** Suggested category */
    suggestedCategory?: string;
  };
  /** Suggested GL account code */
  suggestedGlAccountCode?: string;
}

/**
 * Calculation details for audit trail
 */
export interface ValueDifferenceCalculation {
  /** Expected net amount after withholdings */
  expectedNetAmount?: number;
  /** Detailed withholding calculations */
  calculatedWithholdings?: {
    type: string;
    rate: number;
    base: number;
    amount: number;
  }[];
  /** Late payment calculation details */
  latePaymentCalc?: {
    daysLate: number;
    penaltyRate: number;
    interestRate: number;
    penaltyAmount: number;
    interestAmount: number;
  };
  /** Early payment discount calculation */
  earlyPaymentCalc?: {
    daysEarly: number;
    discountRate: number;
    discountAmount: number;
  };
}

/**
 * Output from the Value Difference Explanation Agent
 */
export interface ValueDifferenceExplanationOutput {
  /** Whether the difference can be explained */
  canExplain: boolean;

  /** Confidence level (0-1) */
  confidence: number;

  /** Identified scenario type */
  scenarioType: ValueDifferenceScenarioType;

  /** Suggested adjustments to reconcile */
  suggestedAdjustments: SuggestedValueAdjustment[];

  /** Detailed explanation */
  reasoning: string;

  /** Recommended action */
  recommendation: ValueDifferenceRecommendation;

  /** Calculation details for audit */
  calculations?: ValueDifferenceCalculation;

  /** Metadata */
  metadata?: {
    /** Model used */
    model?: string;
    /** Processing time in ms */
    processingTimeMs?: number;
  };
}

// ============================================================================
// ZOD SCHEMAS FOR VALIDATION
// ============================================================================

export const SuggestedValueAdjustmentSchema = z.object({
  type: z.nativeEnum(AdjustmentType),
  amount: z.number(),
  description: z.string(),
  createSeparateBill: z
    .object({
      suggested: z.boolean(),
      reason: z.string(),
      suggestedVendorName: z.string().nullable(),
      suggestedCategory: z.string().nullable(),
    })
    .nullable(),
  suggestedGlAccountCode: z.string().nullable(),
});

export const ValueDifferenceCalculationSchema = z.object({
  expectedNetAmount: z.number().nullable(),
  calculatedWithholdings: z
    .array(
      z.object({
        type: z.string(),
        rate: z.number(),
        base: z.number(),
        amount: z.number(),
      }),
    )
    .nullable(),
  latePaymentCalc: z
    .object({
      daysLate: z.number(),
      penaltyRate: z.number(),
      interestRate: z.number(),
      penaltyAmount: z.number(),
      interestAmount: z.number(),
    })
    .nullable(),
  earlyPaymentCalc: z
    .object({
      daysEarly: z.number(),
      discountRate: z.number(),
      discountAmount: z.number(),
    })
    .nullable(),
});

export const ValueDifferenceExplanationOutputSchema = z.object({
  canExplain: z.boolean(),
  confidence: z.number().min(0).max(1),
  scenarioType: z.enum([
    'WITHHOLDING_TAX',
    'LATE_PAYMENT',
    'EARLY_PAYMENT',
    'BANK_FEE',
    'IOF',
    'ROUNDING',
    'PARTIAL_PAYMENT',
    'FX_VARIATION',
    'MARKETPLACE_FEE',
    'NEGOTIATED_DISCOUNT',
    'UNKNOWN',
  ]),
  suggestedAdjustments: z.array(SuggestedValueAdjustmentSchema),
  reasoning: z.string(),
  recommendation: z.enum([
    'RECONCILE_WITH_ADJUSTMENTS',
    'CREATE_SEPARATE_BILLS',
    'MANUAL_REVIEW',
    'REJECT_MATCH',
  ]),
  calculations: ValueDifferenceCalculationSchema.nullable(),
  metadata: z
    .object({
      model: z.string(),
      processingTimeMs: z.number(),
    })
    .nullable(),
});
